<template>
  <rest-page
    :fetch-endpoint="fetchEndpoint"
    class="prizes"
    data-qa="prizes-wrapper"
  >
    <div class="prizes-header" data-qa="prizes-header">
      <el-button
        v-if="isShowExportButton"
        ref="exportBtn"
        type="primary"
        @click="downloadPrizes()"
        :loading="downloadLoading"
        plain
        data-qa="export-prize-button"
        :disabled="false"
      >
        {{ $t('Gamification_Download_Data') }}
      </el-button>

      <el-button
        type="primary"
        @click="handleCreate()"
        data-qa="create-prize-button"
      >
        {{ $t('Gamification_Create_New_Prize') }}
      </el-button>
    </div>

    <game-step-prizes-list
      :segments="segments"
      @edit-prize="handleEditPrize"
      data-qa="prizes-list"
      :game-campaign-id="gameCampaignId"
      :is-leaderboard="isLeaderboard"
      :is-creation-mode="isCreationMode"
      :is-read-only="isReadOnly"
      :save-flag="saveFlag"
      @loaded-prizes-count="handlePrizesCount"
      ref="game-step-prizes-list"
    />

    <game-step-prizes-form-dialog
      v-if="isOpenPrizeFormDialog"
      :dialog-visible="isOpenPrizeFormDialog"
      :model="formValues"
      :is-create="isCreate"
      :game-campaign-id="gameCampaignId"
      :is-leaderboard="isLeaderboard"
      :default-segment-id="defaultSegmentId"
      @close="closeDialog"
      @refetch="refetchPrizes"
      data-qa="prize-form-dialog"
    />
  </rest-page>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'
import GameStepPrizesList from '@/modules/gamification/components/games-campaigns/prizes/GameStepPrizesList.vue'
import GameStepPrizesFormDialog from '@/modules/gamification/components/games-campaigns/prizes/GameStepPrizesFormDialog.vue'
import { castDate } from '@/modules/gamification/helpers'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'GameStepPrizes',

  components: {
    RestPage,
    GameStepPrizesList,
    GameStepPrizesFormDialog
  },

  props: {
    model: {
      type: Object,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      segments: [],
      isOpenPrizeFormDialog: false,
      isCreate: false,
      formValues: {
        name: '',
        criteria_type: null,
        customer_segment_ids: [],
        segment_title: '',
        starts_at: null,
        ends_at: null,
        promotion_id: '',
        promotion_name: '',
        promotion_attributes: {
          promotion_id: null,
          promotion_params: {},
          promotion_amount: null,
          promotion_amount_type: null,
          promotion_amount_attribute_title: null,
          promotion_amount_metric_title: null,
          promotion_amount_property_title: null,
          promotion_amount_window: null,
          promotion_amount_daterange: null,
          promotion_amount_percents: null,
          promotion_amount_round: null,
          promotion_amount_min: null,
          promotion_amount_cap: null,
          promotion_duration: null
        },
        promotion_schema: {},
        range_from: 0,
        range_to: 1
      },
      campaignData: null,
      isShowExportButton: false,
      downloadLoading: false
    }
  },

  computed: {
    fetchEndpoint() {
      return [this.fetchSegmentsEndpoint, this.fetchCampaignData]
    },

    fetchCampaignData() {
      if (!this.gameCampaignId) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-game-campaign/${this.gameCampaignId}`,
        handler: data => {
          this.campaignData = data['data']
          this.formValues.criteria_type = this.campaignData.leaderboard
            ? 'leaderboard'
            : 'points'
        }
      })
    },

    fetchSegmentsEndpoint() {
      const params = {
        'per-page': 50
      }

      if (this.value) {
        params.include_id = this.value
      }

      return projectEndpoint({
        method: 'get',
        url: `segments`,
        params: params,
        handler: data => {
          this.segments = data
        }
      })
    },

    defaultSegmentId() {
      if (!this.campaignData || !this.campaignData.default_segment_id) {
        return null
      }

      return Number(this.campaignData.default_segment_id)
    },

    isLeaderboard() {
      return this.campaignData && this.campaignData.leaderboard
    }
  },

  mounted() {
    this.$emit('checkChanges', false)
    this.$emit('check', false)
  },

  watch: {
    isShowExportButton(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const exportButton = this.$refs.exportBtn
          if (!exportButton) return
          const el = exportButton.$el
          if (!el) return
          el.classList.remove('is-disabled')
        })
      }
    }
  },

  methods: {
    closeDialog() {
      this.formValues = {
        id: null,
        name: '',
        criteria_type: this.isLeaderboard ? 'leaderboard' : 'points',
        customer_segment_ids: [],
        segment_title: '',
        starts_at: null,
        ends_at: null,
        promotion_id: '',
        promotion_name: '',
        promotion_attributes: {
          promotion_id: null,
          promotion_params: {},
          promotion_amount: null,
          promotion_amount_type: null,
          promotion_amount_attribute_title: null,
          promotion_amount_metric_title: null,
          promotion_amount_property_title: null,
          promotion_amount_window: null,
          promotion_amount_daterange: null,
          promotion_amount_percents: null,
          promotion_amount_round: null,
          promotion_amount_min: null,
          promotion_amount_cap: null,
          promotion_duration: null
        },
        promotion_schema: {},
        range_from: 0,
        range_to: 1,
        points: 0
      }
      this.isOpenPrizeFormDialog = false
    },

    handleCreate() {
      this.isCreate = true
      this.isOpenPrizeFormDialog = true
    },

    handleEditPrize(row) {
      this.isOpenPrizeFormDialog = true
      this.isCreate = false

      this.formValues = {
        ...row,
        starts_at: row.starts_at ? castDate(row.starts_at) : null,
        ends_at: row.ends_at ? castDate(row.ends_at) : null
      }
    },
    refetchPrizes() {
      if (this.$refs['game-step-prizes-list']) {
        this.$refs['game-step-prizes-list'].refresh()
      }
    },

    handlePrizesCount(count) {
      this.isShowExportButton = count > 0
    },

    downloadPrizes() {
      this.downloadLoading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/prizes/download`
        })
      )
        .then(({ data }) => {
          this.$message({
            message: data.message,
            type: 'success'
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.downloadLoading = false
        })
    }
  }
}
</script>

<style scoped>
.prizes {
  padding-bottom: 60px;
  background-color: #fff;
}

.prizes-header {
  display: flex;
  padding-bottom: 14px;
  gap: 9px;
  justify-content: end;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/pages/GameStepPrizes.vue